/**
 * Aboutのサービスの札を花が開くように出す
 *
 * @description 硬いエッジのマスクを2倍の大きさで作り、位置をずらして開く。
 *              枠ごと開くので札そのものに掛ける。枠が出来てから中身が下から入る。
 *              下から上へのワイプは使わない。
 */

/** デザインの基準幅。持ち上げ量を画面に合わせるのに使う */
const DESIGN_WIDTH = 1728;
const DESIGN_WIDTH_SP = 393;

/** SPに切り替わる幅。_st-mediaquery.scss の mq() と同じ */
const SP = '(max-width: 1024px)';

/** 花が開く向き。隣どうしで揃えないよう1枚ずつ散らす */
const CARD_DIR = ['left', 'top', 'right', 'bottom', 'left'];

/** 向きごとのマスク。原点は開く側に置いて、そこが動かないようにする */
const DIRS: Record<string, { img: string; size: string; from: number[]; to: number[]; org: string }> = {
    left: { img: 'linear-gradient(to right, #000 0 50%, transparent 50% 100%)', size: '200% 100%', from: [100, 0], to: [0, 0], org: '0% 50%' },
    right: { img: 'linear-gradient(to left, #000 0 50%, transparent 50% 100%)', size: '200% 100%', from: [0, 0], to: [100, 0], org: '100% 50%' },
    top: { img: 'linear-gradient(to bottom, #000 0 50%, transparent 50% 100%)', size: '100% 200%', from: [0, 100], to: [0, 0], org: '50% 0%' },
    bottom: { img: 'linear-gradient(to top, #000 0 50%, transparent 50% 100%)', size: '100% 200%', from: [0, 0], to: [0, 100], org: '50% 100%' },
};

/** 枠が出来上がる進行。中身はこれより後にしか出さない */
const FRAME_END = 0.55;

/** 開きはじめの大きさ */
const BLOOM_FROM = 0.6;

/** 中身が出はじめる進行と、持ち上げる量(px)、1つずつのずらし */
const IN_FROM = 0.44;
const IN_RISE = 24;
const IN_STAG = 0.14;

/** 札どうしのずらし。同時に出すと5枚が一枚の板に見える */
const CARD_STAG = 0.09;

/** 現れきるまでに使う高さ（画面の何割） */
const RANGE = 0.26;

/** 立ち上がりの秒数 */
const SEC = 1.3;

/** 中身の並び。この順に出す */
const PARTS = ['[class$="HeadTtl"]', '[class$="HeadFig"]', '[class$="InnerTxt"]'];

/** 外したい手続き。非同期遷移で節ごと作り直すときに使う */
let detach: (() => void) | null = null;

/**
 * 0..1 に丸める
 *
 * @param v 値
 * @returns 0..1 の値
 */
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * cubic-bezier(0.08, 0.7, 0.58, 1) を解く
 *
 * @description サイト共通の曲線。gsapを持ち込まずに済むよう二分法で解く
 * @param t 0..1 の進行
 * @returns イージング後の値
 */
const ease = (t: number): number => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;

    const [x1, y1, x2, y2] = [0.08, 0.7, 0.58, 1];
    const bx = (u: number): number => 3 * (1 - u) * (1 - u) * u * x1 + 3 * (1 - u) * u * u * x2 + u * u * u;
    const by = (u: number): number => 3 * (1 - u) * (1 - u) * u * y1 + 3 * (1 - u) * u * u * y2 + u * u * u;

    let lo = 0;
    let hi = 1;

    for (let i = 0; i < 20; i += 1) {
        const mid = (lo + hi) / 2;

        if (bx(mid) < t) lo = mid;
        else hi = mid;
    }

    return by((lo + hi) / 2);
};

/**
 * 1フレームぶんの寄せ幅を返す
 *
 * @description 秒数ぶんで95%まで詰める寄せ。cmn-reveal と同じ考え方
 * @param sec 立ち上がりの秒数
 * @returns 0..1 の寄せ幅
 */
const rateOf = (sec: number): number => 1 - Math.pow(0.05, 1 / Math.max(1, sec * 60));

/**
 * 花開きを止める
 *
 * @returns {void}
 */
const destroyServiceBloom = (): void => {
    detach?.();
    detach = null;
};

/**
 * 花開きを始める
 *
 * @returns {void}
 */
const initServiceBloom = (): void => {
    destroyServiceBloom();

    const list = document.querySelector<HTMLElement>('.service__list');

    if (!list) return;

    const cards = Array.from(list.querySelectorAll<HTMLElement>('.service__listItem')).map((el, i) => ({
        el,
        i,
        cur: 0,
        parts: PARTS.map((s) => el.querySelector<HTMLElement>(s)).filter((e): e is HTMLElement => !!e),
    }));

    if (!cards.length) return;

    /**
     * 1枚ぶんの見え方を書く
     *
     * @description 渡すのは素の進行。イージングは各プロパティで一度だけ掛ける。
     *              二重に掛けると曲線が別物になる
     * @param card 札
     * @param p 0..1 の素の進行
     * @returns {void}
     */
    const paint = (card: (typeof cards)[number], p: number): void => {
        const dir = DIRS[CARD_DIR[card.i % CARD_DIR.length]];
        const e = ease(clamp01(p / FRAME_END));
        const at = (a: number[], b: number[], k: number): string => `${(a[k] + (b[k] - a[k]) * e).toFixed(2)}%`;
        const pos = `${at(dir.from, dir.to, 0)} ${at(dir.from, dir.to, 1)}`;

        card.el.style.maskPosition = pos;
        card.el.style.setProperty('-webkit-mask-position', pos);
        card.el.style.transform = `scale(${(BLOOM_FROM + (1 - BLOOM_FROM) * e).toFixed(4)})`;

        // 枠が出来てから中身が下からスッと入る
        const k = window.matchMedia(SP).matches ? DESIGN_WIDTH_SP : DESIGN_WIDTH;
        const rise = (IN_RISE / k) * window.innerWidth;
        const start = Math.max(IN_FROM, FRAME_END);
        const t0 = clamp01((p - start) / Math.max(0.08, 1 - start));
        const span = Math.max(0.2, 1 - IN_STAG * (card.parts.length - 1));

        card.parts.forEach((el, n) => {
            const t = ease(clamp01((t0 - n * IN_STAG) / span));

            el.style.opacity = t.toFixed(3);
            el.style.transform = `translate3d(0, ${((1 - t) * rise).toFixed(1)}px, 0)`;
        });
    };

    cards.forEach((card) => {
        const dir = DIRS[CARD_DIR[card.i % CARD_DIR.length]];

        card.el.style.transformOrigin = dir.org;
        card.el.style.maskImage = dir.img;
        card.el.style.setProperty('-webkit-mask-image', dir.img);
        card.el.style.maskSize = dir.size;
        card.el.style.setProperty('-webkit-mask-size', dir.size);
        card.el.style.maskRepeat = 'no-repeat';
        card.el.style.setProperty('-webkit-mask-repeat', 'no-repeat');
    });

    // 現れ方は装飾なので、モーションを抑える設定では出しきった状態で置く
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        cards.forEach((card) => paint(card, 1));

        return;
    }

    let raf = 0;

    /**
     * いまの位置から進行を決める
     *
     * @returns {void}
     */
    const update = (): void => {
        const h = Math.max(1, window.innerHeight);
        const span = RANGE * h;
        const rs = rateOf(SEC);
        let moving = false;

        cards.forEach((card) => {
            const box = card.el.getBoundingClientRect();
            // 読む順にずらす。札の上端が画面に入ってから使う
            const want = clamp01((h - box.top - box.height * 0.25) / span - card.i * CARD_STAG);
            // 戻さない。一度開いた札はスクロールを戻しても閉じない
            const next = card.cur + (Math.max(want, card.cur) - card.cur) * rs;

            if (Math.abs(next - card.cur) > 0.0005) moving = true;

            card.cur = next > 0.9995 ? 1 : next;
            paint(card, card.cur);
        });

        raf = moving ? requestAnimationFrame(update) : 0;
    };

    const onScroll = (): void => {
        if (!raf) raf = requestAnimationFrame(update);
    };

    update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    detach = () => {
        if (raf) cancelAnimationFrame(raf);

        raf = 0;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        cards.forEach((card) => {
            card.el.style.removeProperty('mask-image');
            card.el.style.removeProperty('-webkit-mask-image');
            card.el.style.removeProperty('mask-position');
            card.el.style.removeProperty('-webkit-mask-position');
            card.el.style.removeProperty('mask-size');
            card.el.style.removeProperty('-webkit-mask-size');
            card.el.style.removeProperty('transform');
            card.el.style.removeProperty('transform-origin');
            card.parts.forEach((el) => {
                el.style.removeProperty('opacity');
                el.style.removeProperty('transform');
            });
        });
    };
};

export { initServiceBloom, destroyServiceBloom };

document.addEventListener('DOMContentLoaded', initServiceBloom);
